import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type {
	PopupData,
	PopupListItem,
	RecordData,
	RequestError,
	Service,
	TeamMember,
} from 'webapp-types';
import { PopupEvent, TeamMemberStatus, UserLevel } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import eq from 'webapp/helpers/eq';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormSelectComponent from 'webapp/components/form/select';
import PopupInnerModalComponent, { type Args } from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Team from 'webapp/models-custom/team';

import type { UserDataForList } from 'webapp/services/model-manager/users';

type Action = 'setNewOwner' | 'removeMember';
type Option = PopupListItem<Action>;

enum RequestAction {
	ADD = 'add',
	REMOVE = 'remove',
}

enum RequestStatus {
	APPROVE = 'approve',
	DENY = 'deny',
}

export default class PopupManageMembersComponent extends PopupInnerModalComponent<PopupData.Teams.Members.Manage> {
	@service('account') declare accountService: Service.Account;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/teams') declare teamsService: Service.ModelManager.Teams;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('store') declare storeService: Service.Store;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	@tracked override submitInProgress = false;
	@tracked selectedUserId: number | null = null;
	@tracked additionalOptionsOpened = false;
	@tracked activeRowId: number | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.TEAM_MEMBER_POPUP_OPENED);
	}

	get addButtonDisabled(): boolean {
		return this.submitInProgress || !this.selectedUserId;
	}

	get model(): Team {
		return this.popupData.model;
	}

	get members(): TeamMember[] {
		return this.model.teamMembers;
	}

	get membersCount(): number {
		return this.members.length;
	}

	get selectedUserIdString(): string | null {
		return this.selectedUserId ? this.selectedUserId.toString() : null;
	}

	get usersList(): UserDataForList[] {
		return this.mmUsersService.activatedAndVerifiedUsersWithoutViewersList.filter(
			(user) => !this.members.find((member) => member.id.toString() === user.value),
		);
	}

	@cached
	get stringifiedOwnerId(): string {
		return this.model.stringifiedOwnerId;
	}

	get normalizedMembers() {
		const currentUserId = this.model.currentUserService.id;
		const users = this.mmUsersService.activatedUsersList;
		const ownerId = this.stringifiedOwnerId;

		return this.members.map((member) => {
			const { id, status } = member;
			const user = users.find((user) => user.value === id.toString());
			const { value, label, label2, avatar } = user || {};

			return {
				id,
				name: label,
				email: label2,
				avatar,
				isOwner: ownerId === value,
				isMe: value === currentUserId,
				hasPendingRequest: status === TeamMemberStatus.REQUEST,
			};
		});
	}

	@cached
	get additionalResultsText(): string {
		const hasAnyViwerRoleUsers = this.mmUsersService.activatedUserRecords.some(
			(user) => user.level === UserLevel.VIEWER,
		);

		return hasAnyViwerRoleUsers ? 'Users with the viewer role cannot be added.' : '';
	}

	getOptions(memberId: number): Option[] {
		const isMemberMe = this.currentUserService.numericId === memberId,
			className = isMemberMe ? '' : 'delete',
			removeIcon = isMemberMe ? 'Logout' : 'Trash',
			removeText = isMemberMe ? 'Leave space' : 'Remove member';

		return [
			{
				icon: 'UserAccess',
				text: 'Set as space owner',
				action: 'setNewOwner',
			},
			{
				class: className,
				icon: removeIcon,
				text: removeText,
				action: 'removeMember',
			},
		];
	}

	@action
	openMemberOptions(memberId: number, e: PointerEvent): void {
		this.activeRowId = this.activeRowId === memberId ? null : memberId;

		if (!this.activeRowId) {
			return;
		}

		const attachTo = (e.target as HTMLElement)?.closest<HTMLElement>('.more-options');
		const popup = this.popupService.createListPopup(attachTo);

		if (popup) {
			popup.open({
				itemsList: this.getOptions(memberId),
				doAction: (actionName: string) => {
					this.doManageMemberActions(actionName as Action, memberId);
				},
			});

			this.additionalOptionsOpened = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.additionalOptionsOpened = false;
					this.activeRowId = null;
				}
			});
		}
	}

	@action
	onMemberChange(userId: string | null): void {
		this.selectedUserId = userId ? Number(userId) : null;
	}

	@action
	doManageMemberActions(actionName: Action, memberId: number): void {
		this[actionName](memberId);
	}

	@action
	addMember(memberId: number | null): void {
		if (!memberId) {
			return;
		}

		const memberIds = this.model.teamMembers.map((member) => member.id);
		const members = [...memberIds, memberId];

		this.analyticsService.event(TrackingEvent.TEAM_MEMBER_CHANGED, {
			type: 'added',
		});

		this.manageMembersRequest(members, RequestAction.ADD);
	}

	@action
	removeMember(memberId: number): void {
		const memberIds = this.model.teamMembers
			.map((member) => member.id)
			.filter((mId) => mId !== memberId);

		const userName = this.normalizedMembers.find((user) => user.id === memberId)?.name || '';

		const confirmAction = () => {
			this.analyticsService.event(TrackingEvent.TEAM_MEMBER_CHANGED, {
				type: 'removed',
			});

			this.manageMembersRequest(memberIds, RequestAction.REMOVE);
		};

		const isUserMe = this.currentUserService.numericId === memberId,
			title = isUserMe
				? `Leave the ${this.model.name} space?`
				: `Remove ${userName} from the ${this.model.name} space?`,
			text = isUserMe
				? 'You`ll lose access to all space-related content.'
				: 'This will remove the user from the space.',
			confirmActionText = isUserMe ? 'Leave space' : 'Remove from space';

		this.popupService.openConfirmPopup({
			onConfirm: confirmAction,
			title,
			text,
			confirmAction: confirmActionText,
			cancelAction: 'Cancel',
		});
	}

	@action
	approveMembersRequest(memberId: number): void {
		this.approveOrDenyMembersRequest(RequestStatus.APPROVE, memberId);
	}

	@action
	denyMembersRequest(memberId: number): void {
		this.approveOrDenyMembersRequest(RequestStatus.DENY, memberId);
	}

	@action
	setNewOwner(memberId: number): void {
		this.submitInProgress = true;

		this.analyticsService.event(TrackingEvent.TEAM_PAGE_EDITED, {
			action: ['owner'],
		});

		const model = this.model;
		model.ownerId = memberId;

		model
			.save()
			.then(
				() => {
					this.infolayerService.ok(
						`New space owner for space ${model.name} has been set`,
					);
				},
				(errorData: RequestError) => {
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService))
			.finally(() => {
				this.submitInProgress = false;
			});
	}

	private manageMembersRequest(userIds: number[], status: RequestAction): void {
		this.submitInProgress = true;

		this.requestService
			.api<RecordData.Payload>('POST', '/teams/members', {
				teamMembers: {
					id: Number(this.model.id),
					userIds,
				},
			})
			.then(
				(payload) => {
					this.storeService.pushPayload(payload);
					this.infolayerService.ok(
						`User has been successfully ${status === RequestAction.REMOVE ? 'removed from' : 'added to'} the space`,
					);
					this.selectedUserId = null;
				},
				(errorData: RequestError) => {
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService))
			.finally(() => {
				this.submitInProgress = false;
			});
	}

	private approveOrDenyMembersRequest(status: RequestStatus, memberId: number): void {
		this.submitInProgress = true;

		this.requestService
			.api<RecordData.Payload>(
				'PUT',
				`/teams/${this.model.id}/join-requests/${memberId}/${status}`,
			)
			.then(
				(payload) => {
					this.storeService.pushPayload(payload);
					this.infolayerService.ok(
						`User's request has been ${status === RequestStatus.APPROVE ? 'approved' : 'denied'}`,
					);
				},
				(errorData: RequestError) => {
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService))
			.finally(() => {
				this.submitInProgress = false;
			});
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Team" @size="lg" />
			Members ({{this.membersCount}})
		</div>
		<div class="content">
			<div class="container">
				<FormSelectComponent
					name="userId"
					@selected={{this.selectedUserIdString}}
					@onChange={{this.onMemberChange}}
					@options={{this.usersList}}
					@placeholder="Select a member"
					@showFloatlabel={{false}}
					@isDisabled={{this.submitInProgress}}
					@isRequired={{true}}
					@isNumeric={{false}}
					@additionalResultsText={{this.additionalResultsText}}
				/>
				<SubmitButtonComponent
					@text="Add"
					@extraClass="add-member"
					@submitInProgress={{this.submitInProgress}}
					@onTrigger={{fn this.addMember this.selectedUserId}}
					@isDisabled={{this.addButtonDisabled}}
				/>
			</div>

			<div class="members-list">
				{{#if this.normalizedMembers.length}}
					<table width="100%" cellspacing="0" cellpadding="0">
						<colgroup>
							<col width="55%" />
							<col width="20%" />
							<col width="25%" />
						</colgroup>
						<tbody>
							{{#each this.normalizedMembers as |member|}}
								<tr class={{if (eq this.activeRowId member.id) "active"}}>
									<td>
										<div class="member-info">
											<AvatarOrInitialsComponent
												@name={{member.name}}
												@avatar={{member.avatar}}
											/>
											<div class="details">
												<span class="text__name">
													{{member.name}}
													{{#if member.isMe}}(you){{/if}}
												</span>
												<span class="text__email">{{member.email}}</span>
											</div>
										</div>
									</td>
									<td class="status">
										{{#if member.isOwner}}
											Space owner
										{{else if member.hasPendingRequest}}
											<span class="pending-label">
												<CommonUiIconComponent @name="Clock" @size="sm" />
												Pending request
											</span>
										{{else}}
											Member
										{{/if}}
									</td>
									<td class="member-actions">
										{{#unless member.isOwner}}
											{{#if member.hasPendingRequest}}
												<a
													role="button"
													class="approve-button"
													{{on
														"click"
														(fn this.approveMembersRequest member.id)
													}}
												>
													Approve
												</a>
												<a
													role="button"
													class="deny-button"
													{{on
														"click"
														(fn this.denyMembersRequest member.id)
													}}
												>
													Deny
												</a>
											{{else}}
												<a
													role="button"
													class="more-options
														{{if
															(eq this.activeRowId member.id)
															'more-option-active'
														}}"
													{{on
														"click"
														(fn this.openMemberOptions member.id)
													}}
												>
													<CommonUiIconComponent
														@name="Ellipsis"
														@size="sm"
													/>
												</a>
											{{/if}}
										{{/unless}}
									</td>
								</tr>
							{{/each}}
						</tbody>
					</table>
				{{else}}
					<div class="empty-state">
						<CommonUiIconComponent @name="Team" @size="lg" />
						<p>No members found</p>
					</div>
				{{/if}}
			</div>
		</div>
	</template>
}
